import HBButtonGroup from "@/components/HBButtonGroup";
import HBIcon from "@/components/HBIcon";
import { useLogin } from "@/components/LoginModal";
import PinCreatorModal from "@/components/PinCreatorModal";
import PinSuccessMessage from "@/components/PinSuccessMessage";
import { EVENT } from "@/constants/event";
import { SHORTCUT } from "@/constants/shortcut";
import useAppStore from "@/context/store";
import { flashPin } from "@/services";
import reporter from "@/tracker/report/reporter";
import { getPage } from "@/tracker/shared/global";
import { getContentType } from "@/utils/getContentType";
import isEditingElement from "@/utils/isEditingElement";
import useOuterClick from "@/utils/useOuterClick";
import useShortcut from "@/utils/useShortcut";
import { Button, Dropdown } from "antd";
import classNames from "classnames";
import _ from "lodash";
import { useRouter } from "next/router";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./index.module.less";
// eslint-disable-next-line no-unused-vars
// let hasCurrentPin = false;
/**
 *
 * @param {{
 *  pin: IPin,
 *  onOk?: (pin: IPin) => void,
 *  current: boolean,
 *  shortcutOrder?: number,
 * }} props
 * @param {*} actionRef
 * @returns
 */
function PinButton({ pin, onOk, current, shortcutOrder = 1 }, actionRef) {
  const [ref, visible, setVisible] = useOuterClick(false);
  const [board, setBoard] = useState(null);
  const [type, setType] = useState("flashTip");
  const router = useRouter();
  const { user, lastBoards, pinIds } = useAppStore(
    ({ user, lastBoards, pinIds }) => ({
      user,
      lastBoards,
      pinIds,
    })
  );
  const pinitTimeoutRef = useRef(false);
  const pinitModalRef = useRef(null);

  const { login } = useLogin();

  const onFlash = useCallback(
    async (check) => {
      if (!user) {
        const result = await login({ title: "扫码登录，收藏图片" });
        if (!result) {
          return;
        }
      }
      if (!pin) {
        return;
      }
      const next = async (lastBoards) => {
        const boardId = lastBoards ? lastBoards[0]?.board_id : null;

        const result = await flashPin({
          via: pin.pin_id,
          text: pin.raw_text,
          board_id: boardId,
          ...(check ? { check } : null),
        });

        if (!result.err) {
          if (result.warning) {
            setBoard(result.pin?.board);
            setType("confirm");
            setVisible(true);
          } else {
            const _pin = result.pin;
            PinSuccessMessage.open({
              pin: _pin,
              onUndo: (pin) => {
                const pinId = pin.pin_id;
                useAppStore.setState(({ pinIds }) => ({
                  pinIds: pinIds.filter((pin) => pin.pin_id === pinId),
                }));
              },
            });

            useAppStore.setState(({ pinIds }) => {
              return {
                pinIds: _.uniq(pinIds.concat([pin.pin_id])),
              };
            });
            onOk && onOk(_pin);
            setVisible(false);
          }
        }
      };

      if (lastBoards?.length > 0) {
        next(lastBoards);
      } else {
        // const result = await runLastBoards();
        // const lastBoards = result.boards || [];
        if (lastBoards.length > 0) {
          next(lastBoards);
        } else {
          // setType("createBoard");
          // setVisible(true);
          next(null);
        }
      }
    },
    [lastBoards, login, onOk, pin, setVisible, user]
  );

  const onCancel = useCallback(() => {
    setVisible(false);
  }, [setVisible]);

  const onFlashEnter = useCallback(() => {
    if (!visible) {
      setType("flashTip");
      setVisible(true);
    }
  }, [setVisible, visible]);

  const onFlashLeave = useCallback(() => {
    if (type === "flashTip") {
      setVisible(false);
    }
  }, [setVisible, type]);

  const onCreate = useCallback(async () => {
    if (!user) {
      const result = await login({ title: "扫码登录，收藏图片" });
      if (!result) {
        return;
      }
    }
    pinitModalRef.current = PinCreatorModal.open({
      pin,
      onOk: (_pin) => {
        PinSuccessMessage.open({
          pin: _pin,
          onUndo: (pin) => {
            const pinId = pin.pin_id;
            useAppStore.setState({
              pinIds: pinIds.filter((pin) => pin.pin_id === pinId),
            });
          },
        });
        useAppStore.setState(({ pinIds }) => ({
          pinIds: _.uniq(pinIds.concat([pin.pin_id])),
        }));
        onOk && onOk(_pin);
        useAppStore.getState().refreshLastBoards();
      },
      afterClose: () => {
        pinitModalRef.current = null;
      },
    });
  }, [login, onOk, pin, pinIds, user]);

  // useEffect(() => {
  //   hasCurrentPin = current;
  //   return () => {
  //     hasCurrentPin = false;
  //   };
  // }, [current]);

  const reportShortcut = useCallback((buttonName) => {
    // 埋点上报
    try {
      const pageName = getPage();

      const trackData = {
        event_id: 39008,
        event_name: "button_click",
        button_name: buttonName,
        button_source: document.getElementById("imageViewerWrapper")
          ? "查看大图页面"
          : "其他页面",
        ...pageName,
      };

      reporter(trackData, { bd_disabled: true });
    } catch (e) {
      // do nothing
    }
  }, []);

  // 快捷键：采集 和 快采
  useShortcut(
    SHORTCUT.PIN,
    (e) => {
      setVisible(false);
      // 关注页面需要记录用户有没有操作
      if (router.pathname === "/follow") {
        window.dispatchEvent(new Event(EVENT.CARD_OPERATE));
      }
      if (e.ctrlKey || e.metaKey) {
        return false;
      }
      const editing = isEditingElement();
      if (editing) {
        return false;
      }

      const quickTime = 300; // 双击判断时长 ms

      if (pinitModalRef.current) {
        pinitModalRef.current.destroy();
        pinitModalRef.current = null;
        return false;
      }

      // 当前
      if (current) {
        const pinitTimeout = pinitTimeoutRef.current;

        // 快采
        if (pinitTimeout) {
          clearTimeout(pinitTimeout);

          // 1. 先设置为中间态 0
          pinitTimeoutRef.current = 0;

          // 2.再延时设置为 false
          setTimeout(() => {
            pinitTimeoutRef.current = false;
          }, quickTime);

          // 埋点上报
          reportShortcut("快捷键快采");

          onFlash(true);
          return false;
        }

        if (pinitTimeout === 0) {
          return false;
        }

        // 转采
        pinitTimeoutRef.current = setTimeout(() => {
          onCreate();

          // 埋点上报
          reportShortcut("快捷键采集");

          // 1. 先设置为中间态 0
          pinitTimeoutRef.current = 0;

          // 2.再延时设置为 false
          setTimeout(() => {
            pinitTimeoutRef.current = false;
          }, quickTime);
        }, quickTime);

        return false;
      }
    },
    { order: shortcutOrder }
  );

  useImperativeHandle(actionRef, () => {
    return {
      create: onCreate,
      flash: onFlash,
    };
  });

  const overlay = useMemo(() => {
    if (type === "confirm") {
      return (
        <div className={styles.modal} ref={ref}>
          <div className={styles.content}>
            <span>该图片已在画板</span>
            <span className={styles.bold}>{board?.title}</span>
            <span>，要再次采集？</span>
          </div>
          <div className={styles.footer}>
            <Button
              className={styles.button}
              type="text"
              size="small"
              onClick={onCancel}
            >
              取消
            </Button>
            <Button
              className={styles.button}
              type="primary"
              size="small"
              onClick={() => onFlash(false)}
            >
              采集
            </Button>
          </div>
        </div>
      );
    }
    // } else if (type === "createBoard") {
    //   return (
    //     <div className={styles.modal} ref={ref}>
    //       <div className={styles.content}>当前无画板，请创建画板</div>
    //       <Form className={styles.form} ref={formRef}>
    //         <Form.Item name="title">
    //           <Input className={styles.input} placeholder="输入画板名称" />
    //         </Form.Item>
    //       </Form>
    //       <div className={styles.footer}>
    //         <Button
    //           className={styles.button}
    //           type="text"
    //           size="small"
    //           onClick={onCancel}
    //         >
    //           取消
    //         </Button>
    //         <Button
    //           className={styles.button}
    //           type="primary"
    //           size="small"
    //           onClick={onCreateBoard}
    //         >
    //           采集
    //         </Button>
    //       </div>
    //     </div>
    //   );
    // }

    return (
      <div className={styles.tooltip} ref={ref}>
        <span>
          {lastBoards?.length > 0 && lastBoards[0]?.title
            ? `快采到：${lastBoards[0]?.title}`
            : "快采到：待归类采集"}
        </span>
      </div>
    );
  }, [board, onCancel, onFlash, ref, type, lastBoards]);

  const isActive = useMemo(() => {
    return pinIds.includes(pin?.pin_id);
  }, [pin, pinIds]);
  return (
    <HBButtonGroup
      className={classNames([styles.action, isActive ? styles.active : null])}
    >
      <Button
        type="primary"
        className={styles.pick}
        onClick={onCreate}
        data-gd-click="button_click"
        data-button-name="采集"
        data-content-type={getContentType(pin?.extra)}
        data-pin-id={pin?.pin_id}
        data-board-id={pin?.board_id}
        data-file-id={pin?.file_id}
        data-ext={JSON.stringify(pin?.ext)}
        title={current ? "采集 [R]" : ""}
      >
        {isActive ? "已采集" : "采集"}
      </Button>

      <Dropdown
        placement="bottomCenter"
        visible={visible}
        overlay={overlay}
        destroyPopupOnHide
      >
        <Button
          type="primary"
          className={styles.flash}
          onClick={() => onFlash(true)}
          onMouseEnter={onFlashEnter}
          onMouseLeave={onFlashLeave}
          data-gd-click="button_click"
          data-button-name="快采"
          data-pin-id={pin?.pin_id}
          data-board-id={pin?.board_id}
          data-file-id={pin?.file_id}
          title={current ? "快采 [R+R]" : ""}
        >
          <HBIcon type="ic_flash" />
        </Button>
      </Dropdown>
    </HBButtonGroup>
  );
}

export default forwardRef(PinButton);
